import React, { useEffect, useCallback } from 'react';
import SocialAccountsModal from '../components/social-accounts-modal/SocialAccountsModal';
import fbBatchRequest from '../utils/fbBatchRequest';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../utils/connect';
import settingsActions from '../actions/settings';
import * as R from 'ramda';
import { Button, LoadingDots } from '@app/ui';
import logEvent from '../utils/logAuthEvents';
import prepareRenderList from '../utils/prepareRenderList';
import SocialMediaLinkageCard from './SocialMediaLinkageCard';

import '../styles/custom-myles-styles.scss';

import instagramLogo from '../images/social_instagram.png';
import { fbSdkURL } from '../utils/apiUrls';

const scopes =
    'instagram_basic,pages_show_list,instagram_manage_insights,read_insights,pages_manage_metadata,pages_read_engagement,pages_read_user_content,ads_management,business_management';

const pullInstagramBusinessId = response => {
    const {
        accounts: { data: instagramAccounts },
        id: userId,
    } = response;
    return instagramAccounts.reduce((acc, currVal) => {
        const {
            access_token: accessToken,
            id: pageId,
            name: pageName,
        } = currVal;
        const instagramId =
            R.path(['instagram_business_account', 'id'], currVal) || null;

        const pageData = {
            instagramId,
            accessToken,
            pageId,
            pageName,
            userId,
        };
        acc[instagramId] = pageData;
        return acc;
    }, {});
};

const formatInstagramAccounts = (data, igAccounts) => {
    return data.reduce((acc, account) => {
        const { accessToken, userId } = igAccounts[account.id];
        const pageData = {
            platformUserId: account.id,
            platformId: 2,
            username: account.username,
            name: account.name,
            profilePictureUrl: account.profile_picture_url,
            type: 'instagram_business',
            id: null,
            profileUrl: `https://www.instagram.com/${account.username}`,
            addedAt: null,
            userId,
            token: {
                accessToken,
                tokenType: 'bearer',
                expire: null,
                revoked: false,
            },
        };
        acc[account.id] = pageData;
        return acc;
    }, {});
};

const InstagramManager = ({
    client,
    getInstagramLinkedAccounts,
    socialAccounts,
    saveIgAccounts,
    triggerModalChange,
    login,
    modal,
    onCloseModal,
    refreshInstagramAccounts,
    removeIgAccount,
}) => {
    const { t } = useTranslation('settings');

    useEffect(() => {
        getInstagramLinkedAccounts(client.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id]);

    const _onLoginSuccess = useCallback(
        async token => {
            // Use the token passed from the parent component instead of relying on Redux state
            const accessToken = token;

            const batch = encodeURI(
                JSON.stringify([
                    {
                        method: 'GET',
                        relative_url:
                            'me?fields=id,name,accounts{instagram_business_account,access_token,app_id}',
                    },
                ]),
            );

            const response = await fetch(
                `${fbSdkURL}/me?limit=100&access_token=${accessToken}&batch=${batch}`,
                {
                    method: 'POST',
                },
            );
            const responseArray = await response.json();

            if (responseArray && responseArray.length > 0) {
                for (const { body } of responseArray) {
                    const data = JSON.parse(body);

                    //eslint-disable-next-line no-prototype-builtins
                    if (data.hasOwnProperty('accounts')) {
                        const igAccounts = pullInstagramBusinessId(data);

                        const requests = Object.keys(igAccounts).reduce(
                            (acc, instagramBusinessId) => {
                                const account = igAccounts[instagramBusinessId];
                                const request = {
                                    method: 'GET',
                                    name: account.instagramId,
                                    relative_url: `${account.instagramId}?fields=profile_picture_url,website,name,username,biography,followers_count,follows_count,ig_id,id,media_count&access_token=${accessToken}`,
                                };

                                if (account.instagramId !== null) {
                                    acc.push(request);
                                }

                                return acc;
                            },
                            [],
                        );

                        const results = await fbBatchRequest(
                            fbSdkURL,
                            accessToken,
                            requests,
                        );

                        const formattedIgAccounts = formatInstagramAccounts(
                            results,
                            igAccounts,
                        );

                        await logEvent({
                            type: 'link-list-all-available-accounts',
                            payload: {
                                platformId: 2,
                                platformName: 'instagram',
                                accounts: formattedIgAccounts,
                            },
                        });

                        refreshInstagramAccounts(formattedIgAccounts);
                    }
                }
            }

            triggerModalChange('pick-instagram-accounts');
        },
        [refreshInstagramAccounts, triggerModalChange],
    );

    const _onLoginFailure = useCallback(() => {
        alert(t('clientConfiguration.social.notifications.genericError'));

        logEvent({
            type: 'link-request-to-3rd-party-failed',
            payload: {
                platformId: 2,
                platformName: 'instagram',
            },
        });
    }, [t]);

    const _onUnlink = useCallback(
        async account => {
            const { id: accountId, platformUserId } = account;
            await removeIgAccount(client.id, accountId, platformUserId);

            logEvent({
                type: 'unlink',
                payload: {
                    accountId,
                    platformUserId,
                    platformId: 2,
                    platformName: 'instagram',
                },
            });
        },
        [client.id, removeIgAccount],
    );

    const _onSubmit = useCallback(
        selectedAccounts => {
            const { accounts, accessToken: userAccessToken } =
                socialAccounts.instagram;

            const generatePayload = selectedAccounts.map(platformUserId => {
                const account = accounts[platformUserId];

                return {
                    clientId: client.id,
                    account: account,
                    accessToken: account.token.accessToken,
                    platformUserId: account.userId,
                    userAccessToken,
                };
            });

            logEvent({
                type: 'link-selected-accounts',
                payload: {
                    accounts: selectedAccounts,
                    platformId: 2,
                    platformName: 'instagram',
                },
            });

            saveIgAccounts(client.id, generatePayload);
            triggerModalChange(null);
        },
        [client.id, socialAccounts.instagram, saveIgAccounts, triggerModalChange],
    );

    const onLogin = useCallback(() => {
        login(scopes, _onLoginSuccess, _onLoginFailure, 'instagram');

        logEvent({
            type: 'link-request-to-3rd-party',
            payload: {
                platformId: 2,
                platformName: 'instagram',
            },
        });
    }, [login, _onLoginSuccess, _onLoginFailure]);

    const { accounts, linkedAccounts, isRequestingLinkedAccounts } =
        socialAccounts.instagram;

    const linkedAccountsKeys = Object.keys(linkedAccounts);
    const instagramAccounts = Object.values(linkedAccounts);
    const renderList = prepareRenderList(instagramAccounts);

    return (
        <div className="_flex-card">
            <div className="gds-card -m-b-0">
                {linkedAccountsKeys.length > 5 && <span className="-shadow" />}

                <div className="gds-card__block gds-card__block--divide-bottom -p-h-3">
                    <p className="gds-text--regular">
                        <i className="fa fa-instagram -m-r-2" />
                        {t('clientConfiguration.social.instagram.title')}
                    </p>
                </div>
                <div className="gds-card__block -p-h-3 -p-t-3 -p-b-1 -overflow-y-scroll -social-account-container">
                    <div className="gds-flex" style={{ height: '100%' }}>
                        {isRequestingLinkedAccounts && <LoadingDots />}

                        {!isRequestingLinkedAccounts && linkedAccountsKeys.length > 0 && (
                            <div
                                className="gds-flex gds-flex--justify-center -m-b-2"
                                style={{ width: '100%' }}
                            >
                                <Button
                                    onClick={onLogin}
                                    size="small"
                                    variant="primary"
                                    style={{ width: '100%' }}
                                >
                                    {t('clientConfiguration.social.instagram.linkAnother')}
                                </Button>
                            </div>
                        )}

                        {!isRequestingLinkedAccounts &&
                            renderList.map(clientAuthorizedAccount => {
                                const account = clientAuthorizedAccount.account;

                                const revoked = clientAuthorizedAccount.revoked;

                                const id = account.id;
                                const name = account.name;
                                const profilePictureUrl =
                                    account.profilePictureUrl;

                                const _unlinkInternal = () => _onUnlink(account);
                                return (
                                    <SocialMediaLinkageCard
                                        key={id}
                                        type="instagram"
                                        logo={profilePictureUrl}
                                        altLogo={instagramLogo}
                                        revoked={revoked}
                                        _link={onLogin}
                                        _unlink={_unlinkInternal}
                                        name={name}
                                    />
                                );
                            })}

                        {!isRequestingLinkedAccounts && linkedAccountsKeys.length === 0 && (
                            <Button
                                onClick={onLogin}
                                size="small"
                                variant="primary"
                                style={{ width: '100%' }}
                            >
                                {t('clientConfiguration.social.instagram.link')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            {modal === 'pick-instagram-accounts' && (
                <SocialAccountsModal
                    onCloseModal={onCloseModal}
                    items={accounts}
                    linkedItems={linkedAccountsKeys}
                    modal={modal}
                    onSubmit={_onSubmit}
                />
            )}
        </div>
    );
};

InstagramManager.propTypes = {
    instagram: PropTypes.object,
    client: PropTypes.object,
    triggerModalChange: PropTypes.func,
    login: PropTypes.func.isRequired,
    modal: PropTypes.string,
    onCloseModal: PropTypes.func.isRequired,
    getInstagramLinkedAccounts: PropTypes.func.isRequired,
    socialAccounts: PropTypes.object.isRequired,
    saveIgAccounts: PropTypes.func.isRequired,
    removeIgAccount: PropTypes.func.isRequired,
    refreshInstagramAccounts: PropTypes.func.isRequired,
};

InstagramManager.defaultProps = {
    socialAccounts: {},
    user: {},
};

export default connect(
    ['socialAccounts', 'client'],
    settingsActions,
)(InstagramManager);
